import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Scale, DollarSign, Target, ShoppingBag, Zap, Send, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import CounterAnimation from './CounterAnimation';
import RegistrationModal from './RegistrationModal';

const WhyImportantSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');
  const [currentReason, setCurrentReason] = useState(0);

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };
  const reasons = [
    {
      icon: AlertTriangle,
      title: 'Prevent Brand Confusion',
      description: 'Without trademark protection, competitors can use similar names or logos, confusing customers and diluting your brand identity. A registered trademark gives you exclusive rights to your mark.',
      statNum: 70,
      statSuffix: '%',
      statLabel: 'of businesses face brand confusion'
    },
    {
      icon: Scale,
      title: 'Legal Enforcement Power',
      description: 'Federal registration provides you with legal standing to sue for infringement in federal court and opens doors to statutory damages and attorney fees recovery.',
      statNum: 3,
      statSuffix: 'x',
      statLabel: 'easier to win infringement cases'
    },
    {
      icon: DollarSign,
      title: 'Protect Your Investment',
      description: "You've invested time and money building your brand. A trademark protects that investment and prevents others from capitalizing on your hard-earned reputation.",
      statNum: 100,
      statPrefix: '$',
      statSuffix: 'K+',
      statLabel: 'average rebranding cost'
    }
  ];

  const nextReason = useCallback(() => setCurrentReason(p => (p + 1) % reasons.length), [reasons.length]);

  useEffect(() => {
    const timer = setInterval(nextReason, 4000);
    return () => clearInterval(timer);
  }, [nextReason]);

  const risks = [
    {
      icon: Target,
      text: 'Someone else registers your brand name first'
    },
    {
      icon: ShoppingBag,
      text: 'Competitors create confusion in the marketplace'
    },
    {
      icon: Zap,
      text: 'You invest in marketing a brand you cannot legally protect'
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            Critical Information
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Why Trademark Registration is Important
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            In today's competitive marketplace, trademark registration isn't just recommended—it's essential for any serious business owner
          </p>
        </div>

        {/* Main Reasons - Desktop Grid */}
        <div className="hidden md:grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {reasons.map((reason, index) => {
            const backgroundImages = [
              'https://images.unsplash.com/photo-1686771416282-3888ddaf249b',
              'https://images.unsplash.com/photo-1763729805496-b5dbf7f00c79',
              'https://images.unsplash.com/photo-1730382624709-81e52dd294d4'
            ];
            
            return (
              <div
                key={index}
                className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 group overflow-hidden"
              >
                {/* Background Image with Overlay */}
                <div className="absolute inset-0 z-0 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                  <img 
                    src={backgroundImages[index]} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="flex items-start mb-6">
                    <div className="w-16 h-16 bg-[#FF035B]/10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6 backdrop-blur-sm border border-[#FF035B]/20">
                      <reason.icon className="w-8 h-8 text-[#FF035B]" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-black mb-4">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main Reasons - Mobile Auto-Slider */}
        <div className="md:hidden mb-16">
          <div className="overflow-hidden">
            <div className="transition-transform duration-500 ease-in-out flex" style={{ transform: `translateX(-${currentReason * 100}%)` }}>
              {reasons.map((reason, index) => (
                <div key={index} className="w-full flex-shrink-0 px-2">
                  <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <div className="flex items-start mb-4">
                      <div className="w-14 h-14 bg-[#FF035B]/10 rounded-xl flex items-center justify-center border border-[#FF035B]/20">
                        <reason.icon className="w-7 h-7 text-[#FF035B]" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-black mb-3">{reason.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{reason.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center items-center gap-4 mt-4">
            <button onClick={() => setCurrentReason(p => (p - 1 + reasons.length) % reasons.length)} className="w-8 h-8 rounded-full bg-[#FF035B] text-white flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex gap-2">
              {reasons.map((_, i) => (
                <button key={i} onClick={() => setCurrentReason(i)} className={`h-2 rounded-full transition-all ${currentReason === i ? 'bg-[#FF035B] w-6' : 'bg-gray-300 w-2'}`} />
              ))}
            </div>
            <button onClick={nextReason} className="w-8 h-8 rounded-full bg-[#FF035B] text-white flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Risk Section */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 md:p-12 border-2 border-red-100">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Header + Risks */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black">
                  The Cost of Waiting
                </h3>
              </div>
              <p className="text-base text-gray-700 mb-6">
                Every day without trademark protection puts your brand at risk. Here's what could happen:
              </p>
              <div className="space-y-4">
                {risks.map((risk, index) => (
                  <div key={index} className="flex items-start gap-3 group cursor-default">
                    <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                      <risk.icon className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-gray-700 pt-1">{risk.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Real-World Impact */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h4 className="text-lg font-bold text-black mb-6 text-center">
                Real-World Impact
              </h4>
              <div className="space-y-6">
                <div className="border-l-4 border-[#FF035B] pl-6">
                  <p className="text-gray-700 italic mb-2">
                    "A competitor started using a name similar to ours. Without trademark protection, we had no legal recourse and had to rebrand entirely."
                  </p>
                  <p className="text-sm text-gray-500">- Sarah M., E-commerce Owner</p>
                </div>
                <div className="border-l-4 border-[#FF035B] pl-6">
                  <p className="text-gray-700 italic mb-2">
                    "We spent $150K on marketing before discovering someone else owned the trademark. Starting over was devastating."
                  </p>
                  <p className="text-sm text-gray-500">- Michael T., Tech Startup Founder</p>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-lg font-bold text-[#FF035B] mb-2">Don't Let This Be You</p>
                <p className="text-gray-600">Start your trademark registration today</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Buttons - Below the entire section */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => openModal('registration')}
            className="bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] text-white px-8 py-4 rounded-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
          >
            <Send className="w-5 h-5" />
            Start Registration
          </button>
          <button
            onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
            className="bg-white border-2 border-[#FF035B] text-[#FF035B] px-8 py-4 rounded-lg font-semibold hover:bg-[#FF035B] hover:text-white transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
          >
            <MessageCircle className="w-5 h-5" />
            Discuss with Attorney
          </button>
        </div>
      </div>

      {/* Registration Modal */}
      <RegistrationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default WhyImportantSection;